import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { RootState } from "../redux/store";
import { Plus } from "lucide-react";
import {
  setLoginModalState,
  setSignupModalState,
} from "../redux/slices/popupModalSlices";
import { DashboardAuthPrompt } from "../components/DashboardAuthPrompt";
import { CarDetailsCard } from "../components/Dashboard/CarDetailsCard";
import { addMyCar, getMyCars, updateMyCar } from "../redux/slices/carSlice";
import { Car } from "../types/car.types";
import { InsuranceDetailsCard } from "../components/Dashboard/InsuranceDetailsCard";
import { ShowCar } from "../components/Dashboard/ShowCars";
import { KMSUpdateCard } from "../components/Dashboard/KMSUpdateCard";
import { useSearchParams } from "react-router-dom";

export default function Dashboard() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const insuranceFormRef = useRef<HTMLDivElement | null>(null);
  const auth = useSelector((state: RootState) => state?.auth);
  const myCars = useSelector((state: RootState) => state?.myCars);

  const [searchParams, setSearchParams] = useSearchParams();
  const [showCarForm, setShowCarForm] = useState(false);
  const [showKMSForm, setShowKMSForm] = useState(false);
  const [showInsuranceForm, setShowInsuranceForm] = useState(false);
  const [selectedCar, setSelectedCar] = useState<Car | null>(null);
  const [selectedCarIndex, setSelectedCarIndex] = useState(0);

  useEffect(() => {
    if (auth.user && auth.user.cars?.length > 0 && !myCars?.cars?.length) {
      dispatch(getMyCars(auth.user.cars) as any);
    }
  }, [auth.user]);

  useEffect(() => {
    if (myCars?.cars?.length > 0 && !selectedCar) {
      let requiredCar = myCars.cars[0];
      const carId = searchParams.get("carId");
      const foundCarIndex = myCars.cars?.findIndex((car) => car?._id === carId);
      if (carId && foundCarIndex !== -1) {
        requiredCar = myCars?.cars?.[foundCarIndex];
      }
      setSelectedCar(requiredCar);
      setSelectedCarIndex(foundCarIndex === -1 ? 0 : foundCarIndex);
      setSearchParams({ carId: requiredCar?._id } as any);
    }
  }, [myCars.cars?.length]);

  useEffect(() => {
    if (!selectedCar) return;

    if (
      selectedCar?.insuranceCompany ||
      selectedCar?.insuranceNumber ||
      selectedCar?.insuranceType
    )
      return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting === true) {
        setTimeout(() => {
          setShowInsuranceForm(true);
          observer.disconnect();
        }, 2000);
      }
    });

    if (insuranceFormRef.current) observer.observe(insuranceFormRef.current);

    return () => observer.disconnect();
  }, [selectedCar]);

  const handleUpdateKms = () => {
    setShowKMSForm(true);
  };

  const handleCarSubmit = (carData: Car) => {
    dispatch(addMyCar(carData) as any);
    setShowCarForm(false);
  };

  const handleCarUpdate = (carData: Partial<Car>) => {
    dispatch(
      updateMyCar({
        updateDetails: carData,
        carId: selectedCar?._id as string,
      }) as any
    );
    setShowInsuranceForm(false);
    setShowKMSForm(false);
  };

  return (
    <div className="animate-fade-in-up relative">
      {auth?.token && auth?.user?.name ? (
        <div className="p-4">
          <div className="animate-fade-in-up p-4 flex justify-end">
            <button
              onClick={() => setShowCarForm(true)}
              className="flex items-center gap-1 bg-secondaryText dark:bg-secondaryTextDark text-primaryText px-3 py-1.5 rounded-md text-sm hover:bg-blue-700 transition"
            >
              <Plus size={14} />
              {t("Add a Car")}
            </button>
          </div>
          {showCarForm && (
            <CarDetailsCard
              onClose={() => setShowCarForm(false)}
              onSubmit={handleCarSubmit}
            />
          )}
          {showInsuranceForm && (
            <InsuranceDetailsCard
              onClose={() => setShowInsuranceForm(false)}
              onSubmit={handleCarUpdate}
            />
          )}

          {showKMSForm && (
            <KMSUpdateCard
              onClose={() => setShowKMSForm(false)}
              onSubmit={handleCarUpdate}
            />
          )}

          <div className="flex gap-3 overflow-x-auto mb-4 pb-2">
            {myCars?.cars?.map((car, index) => (
              <button
                key={car._id}
                onClick={() => {
                  setSelectedCar(car);
                  setSelectedCarIndex(index);
                  setSearchParams({ carId: car?._id } as any);
                }}
                className={`
                  flex items-center gap-2 px-4 py-2 rounded-lg text-sm whitespace-nowrap transition
                  ${
                    selectedCar?._id === car._id
                      ? "bg-secondaryText dark:bg-secondaryTextDark text-primaryText"
                      : "bg-popupLight dark:bg-popupDark text-primaryText dark:text-primaryTextDark"
                  }
                `}
              >
                {car.brand} {car.model}
              </button>
            ))}
          </div>
          {selectedCar && (
            <div key={selectedCar._id}>
              <ShowCar
                selectedCar={myCars?.cars?.[selectedCarIndex]}
                handleUpdateKms={handleUpdateKms}
              />
              <div ref={insuranceFormRef} className="w-full mb-2"></div>
            </div>
          )}
        </div>
      ) : (
        !auth.isLoading && (
          <DashboardAuthPrompt
            onLogin={() => dispatch(setLoginModalState(true))}
            onSignup={() => dispatch(setSignupModalState(true))}
          />
        )
      )}
    </div>
  );
}
